import React from 'react';
import { useSelector } from 'react-redux';
import { FiThumbsUp, FiMessageSquare, FiRepeat, FiSend, FiGlobe, FiLinkedin } from 'react-icons/fi';

function formatPreviewDate() {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(new Date());
}

function LinkedInPreview({ content, mediaUrl, accountName, accountSubtitle, userInitials }) {
  const lines = content.split('\n');
  const showMore = lines.length > 5 || content.length > 300;
  const truncated = showMore ? lines.slice(0, 5).join('\n').slice(0, 300) : content;

  return (
    <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm">
      <div className="p-4 pb-2">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-md">
            {userInitials || 'B'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-zinc-900">{accountName}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{accountSubtitle}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-xs text-zinc-400">{formatPreviewDate()}</span>
              <span className="text-zinc-300">·</span>
              <FiGlobe className="w-3 h-3 text-zinc-400" />
            </div>
          </div>
          <FiLinkedin className="w-5 h-5 text-[#0A66C2] flex-shrink-0" />
        </div>
      </div>

      <div className="px-4 pb-3">
        {content ? (
          <div className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap break-words">
            {truncated}
            {showMore && <span className="text-zinc-400 text-xs ml-1">...see more</span>}
          </div>
        ) : (
          <p className="text-sm text-zinc-300 italic">Your post content will appear here...</p>
        )}
      </div>

      {mediaUrl && (
        <div className="border-t border-zinc-100">
          <img
            src={typeof mediaUrl === 'string' ? mediaUrl : URL.createObjectURL(mediaUrl)}
            alt="Post media"
            className="w-full max-h-80 object-cover"
          />
        </div>
      )}

      <div className="border-t border-zinc-100 px-4 py-1.5">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <span>0 reactions</span>
          <span>0 comments</span>
        </div>
      </div>

      <div className="border-t border-zinc-100 px-2 py-1">
        <div className="flex items-center justify-between">
          {[
            { icon: FiThumbsUp, label: 'Like' },
            { icon: FiMessageSquare, label: 'Comment' },
            { icon: FiRepeat, label: 'Repost' },
            { icon: FiSend, label: 'Send' },
          ].map(({ icon: Icon, label }) => (
            <button key={label} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-zinc-500 rounded-lg hover:bg-zinc-50 transition-colors" disabled>
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PostPreview({
  content = '',
  platform = 'linkedin',
  account = 'business',
  mediaUrl = null,
  className = '',
}) {
  const { adminInfo } = useSelector((state) => state.login);

  const personalName = adminInfo
    ? `${adminInfo.firstName || ''} ${adminInfo.lastName || ''}`.trim() || 'Your Name'
    : 'Your Name';
  const personalInitials = adminInfo
    ? `${adminInfo.firstName?.[0] || ''}${adminInfo.lastName?.[0] || ''}`
    : 'YN';

  const accountName = account === 'personal' ? personalName : 'Bellhopt';
  const accountSubtitle = account === 'personal' ? 'Personal Profile' : 'Company Page';
  const initials = account === 'personal' ? personalInitials : 'B';

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Live Preview</span>
        <span className={`ml-auto px-2 py-0.5 rounded-md text-[10px] font-medium ${
          account === 'personal' ? 'bg-violet-50 text-violet-600' : 'bg-indigo-50 text-indigo-600'
        }`}>
          {account === 'personal' ? 'Personal' : 'Business'}
        </span>
      </div>

      <div className="bg-zinc-100/60 rounded-2xl p-4">
        {platform === 'linkedin' ? (
          <LinkedInPreview
            content={content}
            mediaUrl={mediaUrl}
            accountName={accountName}
            accountSubtitle={accountSubtitle}
            userInitials={initials}
          />
        ) : (
          <div className="text-center py-12 text-sm text-zinc-400">
            {platform} preview coming soon
          </div>
        )}
      </div>
    </div>
  );
}
